import React, {
    FunctionComponent,
    ReactElement,
    useCallback,
    useState,
} from "react";

import {
    Autocomplete,
    Chip,
    TextField,
    Typography,
    styled,
} from "@mui/material";

import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridToolbar,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { format, subDays } from "date-fns";

import { CircularLoader, DataGridFooter, EmptyRecords } from "../../components/common";
import { useGetInstaMartResults, useGetInstaMartSkuIds } from "../../sdk/hooks";
import { ISkuIdOption } from "../../sdk/utils/api";
import { INSTAMART_PINCODES } from "../../utils/constants";

const Root = styled("section")`
    padding: ${({ theme }) => theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const TitleContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Heading = styled(Typography)`
    font-size: ${({ theme }) => theme.spacing(3)};
    font-weight: 500;
`;

const FiltersContainer = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};
    flex-wrap: wrap;
    align-items: center;
`;

const StyledDataGrid = styled(DataGrid)`
    .MuiDataGrid-cell:focus {
        outline: none;
    }
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const EmptyRecordsContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    align-items: center;
`;

const DateFilterContainer = styled("div")`
    width: 200px;
`;

const SkuFilterContainer = styled("div")`
    min-width: 300px;
`;

const PincodeFilterContainer = styled("div")`
    min-width: 200px;
`;

const columns: GridColDef[] = [
    {
        field: "skuId",
        headerName: "SKU ID",
        flex: 0.15,
        minWidth: 130,
    },
    {
        field: "productId",
        headerName: "Product ID",
        flex: 0.15,
        minWidth: 130,
    },
    {
        field: "pincode",
        headerName: "Pincode",
        flex: 0.1,
        minWidth: 80,
    },
    {
        field: "isAvail",
        headerName: "Available",
        flex: 0.1,
        minWidth: 100,
        renderCell: (params: GridRenderCellParams) => (
            <Chip
                label={params.value ? "Yes" : "No"}
                color={params.value ? "success" : "error"}
                size="small"
                variant="outlined"
            />
        ),
    },
    {
        field: "inStock",
        headerName: "In Stock",
        flex: 0.1,
        minWidth: 100,
        renderCell: (params: GridRenderCellParams) => (
            <Chip
                label={params.value ? "Yes" : "No"}
                color={params.value ? "success" : "error"}
                size="small"
                variant="outlined"
            />
        ),
    },
    {
        field: "scrapedAt",
        headerName: "Scraped At",
        flex: 0.2,
        minWidth: 180,
        valueGetter: (params: GridValueGetterParams) =>
            format(new Date(params.row.scrapedAt), "dd-MM-yyyy, p"),
    },
];

interface IFilters {
    startDate: Date;
    endDate: Date;
    skuId: string | null;
    pincode: string | null;
    cursor: string | null;
    direction: "after" | "before";
}

export const InstaMartResultsScreen: FunctionComponent = (): ReactElement => {
    const [filters, setFilters] = useState<IFilters>({
        startDate: subDays(new Date(), 7),
        endDate: new Date(),
        skuId: null,
        pincode: null,
        cursor: null,
        direction: "after",
    });

    const getSkuIdsQuery = useGetInstaMartSkuIds({
        options: {
            queryKey: ["instamart-sku-ids"],
        },
    });

    const getResultsQuery = useGetInstaMartResults({
        limit: 50,
        cursor: filters.cursor ?? undefined,
        direction: filters.direction,
        skuId: filters.skuId ?? undefined,
        pincode: filters.pincode ?? undefined,
        startDate: format(filters.startDate, "yyyy-MM-dd'T'00:00:00.000'Z'"),
        endDate: format(filters.endDate, "yyyy-MM-dd'T'23:59:59.999'Z'"),
        options: {
            queryKey: [
                "instamart-results",
                filters.startDate.toISOString(),
                filters.endDate.toISOString(),
                filters.skuId ?? "",
                filters.pincode ?? "",
                filters.cursor ?? "",
                filters.direction,
            ],
        },
    });

    const handleStartDateChange = useCallback((newValue: Date | null) => {
        if (newValue) {
            setFilters((prev) => ({
                ...prev,
                startDate: newValue,
                cursor: null,
                direction: "after",
            }));
        }
    }, []);

    const handleEndDateChange = useCallback((newValue: Date | null) => {
        if (newValue) {
            setFilters((prev) => ({
                ...prev,
                endDate: newValue,
                cursor: null,
                direction: "after",
            }));
        }
    }, []);

    const handleSkuIdChange = useCallback(
        (_event: React.SyntheticEvent, value: ISkuIdOption | null) => {
            setFilters((prev) => ({
                ...prev,
                skuId: value?.skuId ?? null,
                cursor: null,
                direction: "after",
            }));
        },
        [],
    );

    const handlePincodeChange = useCallback(
        (
            _event: React.SyntheticEvent,
            value: { label: string; value: string } | null,
        ) => {
            setFilters((prev) => ({
                ...prev,
                pincode: value?.value ?? null,
                cursor: null,
                direction: "after",
            }));
        },
        [],
    );

    const handleNavigateBack = useCallback(() => {
        setFilters((prev) => ({
            ...prev,
            cursor: getResultsQuery.data?.cursors?.previous ?? "",
            direction: "before",
        }));
    }, [getResultsQuery.data?.cursors?.previous]);

    const handleNavigateForward = useCallback(() => {
        setFilters((prev) => ({
            ...prev,
            cursor: getResultsQuery.data?.cursors?.next ?? "",
            direction: "after",
        }));
    }, [getResultsQuery.data?.cursors?.next]);

    if (getResultsQuery.isLoading) {
        return <CircularLoader />;
    }

    return (
        <Root>
            <TitleContainer>
                <Heading>Instamart Results</Heading>
            </TitleContainer>

            <FiltersContainer>
                <DateFilterContainer>
                    <DatePicker
                        label="Start Date"
                        value={filters.startDate}
                        maxDate={new Date()}
                        onChange={handleStartDateChange}
                        slotProps={{
                            textField: { size: "small", fullWidth: true },
                        }}
                    />
                </DateFilterContainer>
                <DateFilterContainer>
                    <DatePicker
                        label="End Date"
                        value={filters.endDate}
                        minDate={filters.startDate}
                        maxDate={new Date()}
                        onChange={handleEndDateChange}
                        slotProps={{
                            textField: { size: "small", fullWidth: true },
                        }}
                    />
                </DateFilterContainer>
                <SkuFilterContainer>
                    <Autocomplete
                        options={getSkuIdsQuery.data ?? []}
                        value={
                            getSkuIdsQuery.data?.find(
                                (opt) => opt.skuId === filters.skuId,
                            ) ?? null
                        }
                        onChange={handleSkuIdChange}
                        getOptionLabel={(option) =>
                            `${option.productId} - ${option.variantTitle} (${option.skuId})`
                        }
                        isOptionEqualToValue={(option, value) =>
                            option.skuId === value.skuId
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="SKU ID"
                                size="small"
                            />
                        )}
                        loading={getSkuIdsQuery.isLoading}
                        fullWidth={true}
                    />
                </SkuFilterContainer>
                <PincodeFilterContainer>
                    <Autocomplete
                        options={INSTAMART_PINCODES}
                        value={
                            INSTAMART_PINCODES.find(
                                (pincode) => pincode.value === filters.pincode,
                            ) ?? null
                        }
                        onChange={handlePincodeChange}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Pincode"
                                size="small"
                            />
                        )}
                        fullWidth={true}
                    />
                </PincodeFilterContainer>
            </FiltersContainer>

            {getResultsQuery.data?.records?.length === 0 && (
                <EmptyRecords>
                    <EmptyRecordsContainer>
                        <Title>No results available</Title>
                        <Typography>
                            No InstaMArt results were found for the selected
                            filters.
                        </Typography>
                        <Typography>
                            Try changing the filters or date range.
                        </Typography>
                    </EmptyRecordsContainer>
                </EmptyRecords>
            )}

            {getResultsQuery.isSuccess &&
                getResultsQuery.data?.records?.length > 0 && (
                    <StyledDataGrid
                        autoHeight={true}
                        rows={getResultsQuery.data?.records ?? []}
                        columns={columns}
                        loading={getResultsQuery.isLoading}
                        slots={{
                            toolbar: GridToolbar,
                            footer: () => (
                                <DataGridFooter
                                    hasNextRecords={Boolean(
                                        getResultsQuery.data?.cursors?.next,
                                    )}
                                    hasPreviousRecords={Boolean(
                                        getResultsQuery.data?.cursors?.previous,
                                    )}
                                    onNavigateBack={handleNavigateBack}
                                    onNavigateForward={handleNavigateForward}
                                />
                            ),
                        }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                csvOptions: {
                                    disableToolbarButton: false,
                                },
                                printOptions: {
                                    disableToolbarButton: true,
                                },
                            },
                        }}
                    />
                )}
        </Root>
    );
};
